/**
 * MaizePurchasesTab — Maize purchases from women farmers + receipt download.
 */
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { MapPin, Phone, Scale, Receipt, Pencil, Trash2 } from 'lucide-react';
import { formatCurrency, formatNumber, formatDate } from '../../lib/utils';

const MaizePurchasesTab = ({ purchases = [], onDownloadReceipt, canEdit = false, onEdit, onDelete }) => (
  <Card>
    <CardHeader>
      <CardTitle className="font-heading">Maize Purchases from Women Farmers</CardTitle>
      <CardDescription>Complete list of all maize bought with farmer details</CardDescription>
    </CardHeader>
    <CardContent className="p-0">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Date</TableHead>
            <TableHead>Farmer Name</TableHead>
            <TableHead>Contact</TableHead>
            <TableHead>Village</TableHead>
            <TableHead>Quantity</TableHead>
            <TableHead>Price/kg</TableHead>
            <TableHead>Grade</TableHead>
            <TableHead className="text-right">Total Paid</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {purchases.length === 0 ? (
            <TableRow>
              <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                No purchases recorded yet
              </TableCell>
            </TableRow>
          ) : (
            purchases.map((purchase) => (
              <TableRow key={purchase.id} className="table-row-hover">
                <TableCell>{formatDate(purchase.date)}</TableCell>
                <TableCell className="font-medium">{purchase.farmer_name}</TableCell>
                <TableCell>
                  <span className="flex items-center gap-1 text-sm">
                    <Phone className="h-3 w-3 text-muted-foreground" />
                    {purchase.farmer_phone || '-'}
                  </span>
                </TableCell>
                <TableCell>
                  <span className="flex items-center gap-1 text-sm">
                    <MapPin className="h-3 w-3 text-muted-foreground" />
                    {purchase.farmer_village || '-'}
                  </span>
                </TableCell>
                <TableCell>
                  <span className="flex items-center gap-1">
                    <Scale className="h-3 w-3 text-muted-foreground" />
                    {formatNumber(purchase.quantity_kg)} kg
                  </span>
                </TableCell>
                <TableCell>{formatCurrency(purchase.price_per_kg)}</TableCell>
                <TableCell>
                  <Badge variant="outline">Grade {purchase.quality_grade}</Badge>
                </TableCell>
                <TableCell className="text-right font-medium text-green-600">
                  {formatCurrency(purchase.total_amount)}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1.5 flex-wrap">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => onDownloadReceipt?.(purchase.id)}
                      data-testid={`download-receipt-${purchase.id}`}
                      title="Download payment receipt PDF"
                      className="h-8 px-2"
                    >
                      <Receipt className="h-4 w-4" />
                    </Button>
                    {canEdit && (
                      <>
                        <Button size="sm" variant="outline" onClick={() => onEdit?.(purchase)} data-testid={`edit-purchase-${purchase.id}`} className="h-8 px-2.5">
                          <Pencil className="h-3.5 w-3.5 mr-1" />Edit
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => onDelete?.(purchase)} data-testid={`delete-purchase-${purchase.id}`} className="h-8 px-2.5 border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive">
                          <Trash2 className="h-3.5 w-3.5 mr-1" />Delete
                        </Button>
                      </>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </CardContent>
  </Card>
);

export default MaizePurchasesTab;
